<div mat-dialog-title class="d-flex justify-content-between align-items-center">
  <h2 class="h1" data-automation-id="dialog-title">{{ entity }} Lookup</h2>
  <button
    data-automation-id="dialog-close-button"
    mat-icon-button
    [mat-dialog-close]
    aria-label="Close {{ entity }} Lookup dialog"
  >
    <mat-icon>close</mat-icon>
  </button>
</div>

<mat-dialog-content>
  <p>
    Please provide some information in the fields below to locate the {{ entity.toLowerCase() }} you
    are searching for.
  </p>
  <form [formGroup]="genericEntitySearchForm">
    <div class="row">
      <mat-form-field class="col-6">
        <input
          matInput
          formControlName="name"
          placeholder="{{ entity }} Name"
          minlength="3"
          maxlength="50"
        />
        <mat-error *ngIf="genericEntitySearchForm.get('name').hasError('minlength')">
          {{ entity }} Name requires a minimum of three characters for search.
        </mat-error>
      </mat-form-field>
      <mat-form-field class="col-6">
        <input
          matInput
          formControlName="fein"
          placeholder="FEIN"
          mask="00-0000000"
          pattern="^(\d{9})$"
        />
        <mat-error *ngIf="genericEntitySearchForm.get('fein').hasError('pattern')">
          FEIN search must be by the full 9 digit FEIN.
        </mat-error>
      </mat-form-field>
    </div>
    <div class="row">
      <mat-form-field class="col-12">
        <input
          matInput
          formControlName="addressLine1"
          placeholder="Address 1"
          minlength="3"
          maxlength="40"
        />
        <mat-error *ngIf="genericEntitySearchForm.get('addressLine1').hasError('minlength')">
          Address 1 requires a minimum of three characters for search.
        </mat-error>
      </mat-form-field>
    </div>
    <div class="row">
      <mat-form-field class="col-12">
        <input
          matInput
          formControlName="addressLine2"
          placeholder="Address 2"
          minlength="3"
          maxlength="40"
        />
        <mat-error *ngIf="genericEntitySearchForm.get('addressLine2').hasError('minlength')">
          Address 2 requires a minimum of three characters for search.
        </mat-error>
      </mat-form-field>
    </div>
    <div class="row">
      <mat-form-field class="col-4">
        <input matInput formControlName="city" placeholder="City" minlength="3" maxlength="15" />
        <mat-error *ngIf="genericEntitySearchForm.get('city').hasError('minlength')">
          City requires a minimum of three characters for search.
        </mat-error>
      </mat-form-field>
      <mat-form-field class="col-4">
        <input matInput formControlName="state" placeholder="State" minlength="3" />
        <mat-error *ngIf="genericEntitySearchForm.get('state').hasError('minlength')">
          State requires a minimum of three characters for search.
        </mat-error>
      </mat-form-field>
      <mat-form-field class="col-4">
        <input
          matInput
          formControlName="postalCode"
          placeholder="ZIP Code"
          pattern="^(\d{5,9})$"
          maxlength="9"
        />
        <mat-error *ngIf="genericEntitySearchForm.get('postalCode').hasError('pattern')">
          ZIP Code must be 5-9 digits long for search.
        </mat-error>
      </mat-form-field>
    </div>
    <div class="d-flex flex-row">
      <button
        type="button"
        mat-flat-button
        color="primary"
        class="mr-3 mt-3"
        aria-label="Search"
        [disabled]="!genericEntitySearchForm.valid"
        (click)="handleSearch(this.genericEntitySearchForm.value)"
      >
        <mat-icon>search</mat-icon>
        <span>Search</span>
      </button>
      <button
        type="button"
        mat-flat-button
        color="primary"
        class="mr-3 mt-3"
        (click)="handleReset()"
      >
        Clear
      </button>
      <button type="button" mat-flat-button color="warn" class="mt-3" [mat-dialog-close]>
        Cancel
      </button>
    </div>
  </form>

  <section [hidden]="!hasSearched">
    <hr />
    <h2>Results</h2>
    <p *ngIf="!searchResults.total">No results found. Please revise your search and try again.</p>
    <div *ngIf="searchResults.total" class="table-responsive-sm">
      <p>
        The results below display only the Primary Address associated to an Entity. If you searched
        for a different address and do not see it in the table, that means we have it on record as a
        non-primary address for the entities shown below.
      </p>
      <p *ngIf="searchResults.total > 5" class="text-warn">
        We found too many results matching your search criteria. Please provide additional criteria
        in the fields above to narrow your results.
      </p>
      <table class="table">
        <thead>
          <tr>
            <th>{{ entity }} Name</th>
            <th>Street Address</th>
            <th>City, State, Zip</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr
            *ngFor="let entity of searchResults.data"
            (click)="handleEntitySelect(entity)"
            [class.selected]="entity === selectedRow"
          >
            <td data-automation-id="generic-entity-search_202130142406">
              {{ entity.name }}
              <span *ngIf="entity.doingBusinessAs">
                <br />
                DBA: {{ entity.doingBusinessAs }}
              </span>
            </td>
            <td data-automation-id="generic-entity-search_202130142412">
              {{ entity.addressLine1 }}
              <span *ngIf="entity.addressLine2">
                <br />
                {{ entity.addressLine2 }}
              </span>
            </td>
            <td data-automation-id="generic-entity-search_202130142422">
              <span *ngIf="entity.city">{{ entity.city }}</span>
              <span *ngIf="entity.state">, {{ entity.state }}</span>
              <span *ngIf="entity.country && entity.countryId !== countries.UnitedStates">
                , {{ entity.country }}
              </span>
              <span *ngIf="entity.zip">
                <br />
                {{ entity.zip }}
              </span>
            </td>
            <td data-automation-id="generic-entity-search_202130142426">{{ entity.status }}</td>
          </tr>
        </tbody>
      </table>
      <div class="dialog-footer">
        <button
          mat-flat-button
          color="primary"
          data-automation-id="generic-entity-search_202130141951"
          [disabled]="!selectedRow"
          [mat-dialog-close]="selectedRow"
          aria-label="Confirm {{ entity }} Lookup"
        >
          Confirm
        </button>
        <button
          mat-flat-button
          color="warn"
          data-automation-id="generic-entity-search_202130141954"
          [mat-dialog-close]
          aria-label="Close {{ entity }} Lookup dialog"
        >
          Cancel
        </button>
      </div>
    </div>
  </section>
</mat-dialog-content>
